import { Dialog, DialogContent, DialogHeader, DialogTitle } from "frontend-components";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";

import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";

import { ConfirmDiscardDialog } from "../ConfirmDiscardDialog";

import { TaskFormBody, type TaskFormBodyHandle } from "./TaskFormBody";
import { useTaskLinks } from "./useTaskLinks";

export function TaskModalCreateView({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const formRef = useRef<TaskFormBodyHandle | null>(null);
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
    const [pendingRemoveLinkId, setPendingRemoveLinkId] = useState<number | null>(null);

    const links = useTaskLinks({
        initialLinks: [],
        isEditMode: false,
        onLocalChange: () => formRef.current?.markDirty(),
    });

    const requestClose = () => {
        if (formRef.current?.isDirty()) {
            setConfirmDiscardOpen(true);
            return;
        }
        onClose();
    };

    const confirmRemoveLink = () => {
        if (pendingRemoveLinkId !== null) {
            links.remove(pendingRemoveLinkId);
            setPendingRemoveLinkId(null);
        }
    };

    return (
        <>
            <Dialog
                open
                onOpenChange={(open) => {
                    if (!open) requestClose();
                }}
            >
                <DialogContent className="bg-compendia-card max-h-[90vh] w-[calc(100vw-2rem)] sm:max-w-[700px] overflow-y-auto overflow-x-hidden text-compendia-foreground">
                    <DialogHeader className="-mx-6 pl-6 pr-24 pb-4 border-b border-compendia-border min-w-0">
                        <DialogTitle className="text-lg font-bold text-compendia-foreground leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                            {t("task.newActivity")}
                        </DialogTitle>
                    </DialogHeader>
                    <TaskFormBody
                        key="new"
                        ref={formRef}
                        task={null}
                        links={links}
                        onRequestRemoveLink={setPendingRemoveLinkId}
                        onCancel={requestClose}
                        onSaved={() => onClose()}
                    />
                </DialogContent>
            </Dialog>

            <ConfirmDiscardDialog
                open={confirmDiscardOpen}
                onOpenChange={setConfirmDiscardOpen}
                onConfirm={onClose}
            />

            <ConfirmDeleteDialog
                open={pendingRemoveLinkId !== null}
                onOpenChange={(open) => {
                    if (!open) setPendingRemoveLinkId(null);
                }}
                onConfirm={confirmRemoveLink}
                title={t("common.removeLink")}
                description={t("common.confirmRemoveLink")}
            />
        </>
    );
}
